import React, { useMemo, useState, useCallback, useEffect } from "react";
import { Row, Col } from "react-bootstrap";
import { ColDef } from "ag-grid-community";
import { ThreeCircles } from "react-loader-spinner";
import * as Icon from "react-bootstrap-icons";
import RulesModal from "./components/RulesModal/RulesModal";
import { fetchTemplateFieldRules } from "./RulesPage.service";
import { getGridStyle, ModelMode } from "./RulesPage.utils";
import ActionRenderer from "./components/ActionsRenderer/ActionsRenderer";
import styles from "./RulesPage.module.css";
import CustomButton from "../../reusable_components/custom_button_component/Button";
import { TimeRenderer } from "../../client_onboarding/ClientsGrid/ClientsGrid.utils";
import CygnusGrid from "../../../pages/reusable_components/CygnusGrid/CygnusGrid";

import "../EngagementLetter.css";
import { getAllFields } from "../../../pages/client_onboarding/MDMFieldsConfig/MDMFieldsConfig.service";
import { TemplateRuleRow } from "./components/RulesModal/RulesModal.types";
import { SelectedFieldOptionType } from "../../../pages/firm_setup/RulesModal/RulesModal.types";

const RulesPage: React.FC = () => {
  // State variables
  const [refreshGrid, setRefreshGrid] = useState<boolean>(false);
  const [rulesDetails, setRulesDetails] = useState<TemplateRuleRow[]>([]);
  const [rulesModalShow, setRulesModalShow] = useState<boolean>(false);

  const [selectedRule, setSelectedRule] = useState<TemplateRuleRow | null>(null);
  const [modelMode, setModelMode] = useState<ModelMode>("create");
  const [key, setKey] = useState<number | undefined>(undefined);
  const [selectedFieldsList, setSelectedFieldsList] = useState<SelectedFieldOptionType[]>([]);

  // Fetch rules data
  const fetchRulesPageData = useCallback(async () => {
    resetState();
    setRefreshGrid(true);

    try {
      const details = await fetchTemplateFieldRules();
      setRulesDetails(details);
      setKey(details.length);
    } finally {
      setRefreshGrid(false);
    }
  }, []);

  // Load data on mount
  useEffect(() => {
    fetchRulesPageData();
  }, []);

  // Column definitions
  const defaultRulesColumnDefs = useMemo<ColDef[]>(() => [
    {
      headerName: "Rule Name",
      field: "RuleName",
      sortable: false,
      flex: 3,
    },
    {
      headerName: "Updated By",
      field: "UpdatedBy",
      sortable: false,
      flex: 1,
    },
    {
      headerName: "Created Date",
      field: "CreatedDate",
      cellRenderer: TimeRenderer,
      sortable: false,
      flex: 1,
    },
    {
      headerName: "Updated Date",
      field: "UpdatedDate",
      cellRenderer: TimeRenderer,
      sortable: false,
      flex: 1,
    },
    {
      headerName: "Action",
      pinned: "right",
      cellRenderer: (params: any) => (
        <ActionRenderer
          data={params.data}
          onEdit={(data) => handleRuleShow("edit", data)}
          onDelete={fetchRulesPageData}
        />
      ),
      maxWidth: 90,
      minWidth: 90,
      sortable: false,
    },
  ], []);

  const [rulesColumnDefs, setRulesColumnDefs] =
    useState<ColDef[]>(defaultRulesColumnDefs);

  // Reset state
  const resetState = () => {
    setRefreshGrid(false);
    setRulesDetails([]);
    setRulesColumnDefs(defaultRulesColumnDefs);
    setSelectedRule(null);
    setModelMode("create");
  };

  // Show modal
  const handleRuleShow = (
    mode: ModelMode,
    data: TemplateRuleRow | null = null
  ) => {
    setSelectedRule(data);
    setModelMode(mode);
    setRulesModalShow(true);
  };

  // Hide modal
  const handleRuleHide = (shouldRefresh?: boolean) => {
    setRulesModalShow(false);
    setSelectedRule(null);
    if (shouldRefresh) {
      fetchRulesPageData();
    }
  };

  useEffect(() => {
    const init = async () => {
        const fields = await getAllFields();
        const selectedFieldData: SelectedFieldOptionType[] = fields.map(item => ({
            id: item.field_code,
            label: item.field_name,
            value: item.field_value
        }));
        setSelectedFieldsList(selectedFieldData);
    };
    init();
  }, []);

  return (
    <>
      {rulesModalShow && (
        <RulesModal
          mode={modelMode}
          ruleData={selectedRule!!}
          selectedFieldsList={selectedFieldsList}
          onHide={handleRuleHide}
        />
      )}

      <Row className="no-gutter">
        <p className={styles.pageTitle + " display-6"}>Template Rules</p>
      </Row>

      <Row className="no-gutter">
        <Col md={12} className="d-flex justify-content-end">
          <div className={styles.createButtonContainer}>
            <CustomButton
              content={"Create Rule"}
              onclick={() => handleRuleShow("create")}
            />
          </div>
          <div className={styles.refreshContainer}>
            {refreshGrid ? (
              <ThreeCircles
                  height="20"
                  width="20"
                  color="#4fa94d"
                  wrapperStyle={{}}
                  wrapperClass=""
                  visible={true}
                  ariaLabel="three-circles-rotating"
                  outerCircleColor="var(--theme1_tertiary)"
                  innerCircleColor="var(--theme1_secondary)"
                  middleCircleColor="var(--theme1_primary)"
                />
            ) : (
              <Icon.ArrowCounterclockwise
                onClick={fetchRulesPageData}
                className={styles.refreshIcon}
              />
            )}
          </div>
        </Col>
      </Row>

      <Row className="no-gutter">
        <Col md={12}>
          <div className="ag-theme-balham" style={getGridStyle()}>
            <CygnusGrid
              key={key}
              fetchData={async () => ({
                records: rulesDetails,
                totalCount: rulesDetails.length,
              })}
              rowModelType="clientSide"
              dataIdentifier="TemplateRuleId"
              cachedData={rulesDetails}
              setCachedData={setRulesDetails}
              columnDefs={rulesColumnDefs}
              cachedIsLoading={refreshGrid}
              onLoadingChange={setRefreshGrid}
              rowSelection={undefined}
            />
          </div>
        </Col>
      </Row>
    </>
  );
};

export default RulesPage;
